import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu, X, Instagram, Facebook } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";

const LOGO = "https://i.ibb.co/y9NZQnr/IMG-20260521-WA0000.jpg";

const NAV = [
  { label: "All Jewellery", to: "/shop" },
  { label: "Personalized", to: "/shop?is_personalized=true" },
  { label: "Lab Grown", to: "/shop?is_lab_grown=true" },
  { label: "Make Your Own", to: "/make-your-own" },
  { label: "Auctions", to: "/auctions" },
  { label: "Contact", to: "/contact" },
];

const MEGA = {
  "Gemstone": ["Diamond", "Lab Diamond", "Emerald", "Ruby", "Sapphire", "Aquamarine", "Citrine", "Garnet", "Peridot", "Amethyst"],
  "Metal": ["Silver", "Platinum", "9K Gold", "10K Gold", "14K Gold", "18K Gold"],
  "Color": ["Yellow Gold", "White Gold", "Rose Gold"],
  "Occasion": ["Wedding", "Engagement", "Anniversary", "Birthday", "Vacation", "Office Wear"],
};

export default function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--ja-cream)" }}>
      {/* Announcement */}
      <div className="bg-black text-[11px] tracking-[0.3em] uppercase" data-testid="announcement-bar"
           style={{ color: "var(--ja-champagne)" }}>
        <div className="ja-container py-2.5 overflow-hidden">
          <div className="flex items-center justify-center gap-12 whitespace-nowrap">
            <span>Complimentary Shipping Worldwide</span>
            <span className="opacity-50">·</span>
            <span>Certified Luxury jewelry</span>
            <span className="opacity-50">·</span>
            <span>BIS Hallmark Certified</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-xl bg-white/85 border-b" : "bg-transparent"}`}
        style={{ borderColor: "var(--ja-border)" }}
        data-testid="site-header"
      >
        <div className="ja-container">
          <div className="flex items-center justify-between h-20">
            {/* Mobile toggle */}
            <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Nav left */}
            <nav className="hidden md:flex items-center gap-8">
              {NAV.slice(0, 3).map((n) => (
                <NavLink key={n.label} to={n.to} className="ja-link" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </NavLink>
              ))}
              <button
                className="ja-link"
                onMouseEnter={() => setMegaOpen(true)}
                onClick={() => setMegaOpen(!megaOpen)}
                data-testid="mega-menu-trigger"
              >
                Shop By
              </button>
            </nav>

            {/* Logo */}
            <Link to="/" className="flex flex-col items-center" data-testid="brand-logo">
              <img src={LOGO} alt="Jewels Ashu" className="h-12 w-12 object-cover rounded-full" style={{ border: "1px solid var(--ja-border)" }} />
              <span className="ja-serif text-[11px] tracking-[0.4em] uppercase mt-1" style={{ color: "var(--ja-black)" }}>Jewels Ashu</span>
            </Link>

            {/* Nav right */}
            <div className="flex items-center gap-5">
              <button className="hidden md:inline-flex" data-testid="search-button" aria-label="Search">
                <Search size={18} />
              </button>
              <Link to="/wishlist" className="relative" data-testid="wishlist-icon" aria-label="Wishlist">
                <Heart size={18} />
                {wishlist.length > 0 && (
                  <span className="absolute -top-2 -right-2 text-[10px] bg-black text-white w-4 h-4 flex items-center justify-center rounded-full">{wishlist.length}</span>
                )}
              </Link>
              <Link to={user ? "/account" : "/login"} data-testid="account-icon" aria-label="Account"><User size={18} /></Link>
              <Link to="/cart" className="relative" data-testid="cart-icon" aria-label="Cart">
                <ShoppingBag size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 text-[10px] bg-black text-white w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">{cartCount}</span>
                )}
              </Link>
            </div>
          </div>

          {/* Mega menu */}
          {megaOpen && (
            <div
              className="hidden md:block absolute left-0 right-0 top-full bg-white border-t border-b ja-fade-up"
              style={{ borderColor: "var(--ja-border)" }}
              onMouseLeave={() => setMegaOpen(false)}
              data-testid="mega-menu"
            >
              <div className="ja-container grid grid-cols-4 gap-12 py-12">
                {Object.entries(MEGA).map(([head, items]) => (
                  <div key={head}>
                    <p className="ja-overline mb-6">By {head}</p>
                    <ul className="space-y-3">
                      {items.map((it) => (
                        <li key={it}>
                          <Link to={`/shop?${head.toLowerCase()}=${encodeURIComponent(it)}`} className="text-sm hover:underline" data-testid={`mega-${head.toLowerCase()}-${it.toLowerCase().replace(/\s+/g, "-")}`}>
                            {it}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t" style={{ borderColor: "var(--ja-border)" }} data-testid="mobile-drawer">
            <div className="ja-container py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} className="ja-link" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</Link>
              ))}
              {Object.entries(MEGA).map(([head, items]) => (
                <details key={head} className="border-t pt-3" style={{ borderColor: "var(--ja-border)" }}>
                  <summary className="ja-overline cursor-pointer">{head}</summary>
                  <ul className="mt-3 space-y-2">
                    {items.map((it) => (
                      <li key={it}>
                        <Link to={`/shop?${head.toLowerCase()}=${encodeURIComponent(it)}`} className="text-sm">{it}</Link>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
              {user?.role === "admin" && <Link to="/admin" className="ja-link">Admin</Link>}
              {user ? (
                <button onClick={logout} className="ja-link text-left" data-testid="mobile-logout">Sign out</button>
              ) : (
                <Link to="/login" className="ja-link">Sign in</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-32 border-t" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }} data-testid="site-footer">
        <div className="ja-container py-20">
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <h3 className="ja-serif text-4xl tracking-tight">Jewels Ashu</h3>
              <p className="mt-4 text-sm max-w-sm" style={{ color: "var(--ja-muted)" }}>
                Heirloom craftsmanship meets contemporary luxury. Hand-finished in our atelier, certified worldwide.
              </p>
              <form
                className="mt-8 flex border-b"
                style={{ borderColor: "var(--ja-border)" }}
                onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); window.dispatchEvent(new CustomEvent("ja-newsletter", { detail: fd.get("email") })); e.target.reset(); }}
                data-testid="newsletter-form"
              >
                <input name="email" type="email" required placeholder="Your email address" className="flex-1 bg-transparent py-3 text-sm focus:outline-none" data-testid="newsletter-input" />
                <button type="submit" className="ja-overline px-2" data-testid="newsletter-submit">Subscribe</button>
              </form>
            </div>
            {[
              { h: "Shop", l: [["All Jewellery", "/shop"], ["New Arrivals", "/shop?is_new=true"], ["Best Sellers", "/shop?is_best_seller=true"], ["Lab Grown", "/shop?is_lab_grown=true"], ["Auctions", "/auctions"]] },
              { h: "Atelier", l: [["Make Your Own", "/make-your-own"], ["Our Story", "/contact"], ["The Atelier", "/contact"], ["Press", "/contact"]] },
              { h: "Care", l: [["Contact", "/contact"], ["Shipping", "/contact"], ["Returns", "/contact"], ["Hallmark", "/contact"]] },
            ].map((col) => (
              <div key={col.h}>
                <p className="ja-overline mb-6">{col.h}</p>
                <ul className="space-y-3">
                  {col.l.map(([t, to]) => (
                    <li key={t}><Link to={to} className="text-sm" data-testid={`footer-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4 text-xs" style={{ borderColor: "var(--ja-border)", color: "var(--ja-muted)" }}>
            <span>© {new Date().getFullYear()} Jewels Ashu. All Rights Reserved.</span>
            <div className="flex items-center gap-6">
              <Instagram size={14} /><Facebook size={14} />
              <span>India ₹ INR</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
